// This file transforms Shopify product data into the format expected by the Grocerize application

// ItemData and ItemPricings are declared globally in src/types/shopify.ts

/**
 * Format cup volume to be more user friendly
 * Converts uppercase units to lowercase (e.g., 1ML -> 1ml)
 * Makes special replacements like EA -> each
 * @param volumeText Original volume text
 * @returns Formatted volume text
 */
function formatCupVolume(volumeText: string): string {
  if (!volumeText) return '';
  
  // Replace EA with 'each'
  if (/\d+\s*EA/i.test(volumeText)) {
    return volumeText.replace(/EA/i, ' ea');
  }
  
  // For other units, extract number and unit, then lowercase the unit
  const match = volumeText.match(/(\d+(?:\.\d+)?)\s*([A-Za-z]+)/);
  if (match) {
    const [, number, unit] = match;
    return `${number}${unit.toLowerCase()}`;
  }
  
  // If no pattern matches, return the original but lowercase
  return volumeText.toLowerCase();
}

/**
 * Transforms a Shopify product into the ItemData format used by the application
 */
export const transformShopifyProductToItemData = (shopifyProduct: any): ItemData => {
  try {
    // console.log('TRANSFORMER: Starting transformation for product:', shopifyProduct?.id, shopifyProduct?.title);
    // console.log('Transforming Shopify product:', shopifyProduct.id, shopifyProduct.title);
    
    // Extract product ID from the Shopify ID format (gid://shopify/Product/1234567890)
    let numericId = 0;
    try {
      const idParts = shopifyProduct.id.toString().split('/');
      const idStr = idParts[idParts.length - 1];
      numericId = parseInt(idStr, 10) || 0;
      // console.log('Extracted numeric ID:', numericId);
    } catch (error) {
      console.error('Error extracting numeric ID from Shopify product:', error);
      numericId = Math.floor(Math.random() * 1000000); // Fallback to random ID
    }

    // Extract variant information for Coles and Woolworths
    const variants = shopifyProduct.variants || [];
    const itemPricing: ItemPricings[] = [];
    
    // console.log(`Product ${shopifyProduct.title} has ${variants.length} variants`);
    
    // Check if there are variants with supplier metadata
    let hasColesVariant = false;
    let hasWoolworthsVariant = false;
    
    // Process each variant to extract supplier information
    for (const variant of variants) {
      // console.log('Processing variant:', variant.title, 'price:', variant.price);
      
      // Extract supplier information from variant title or options
      const variantTitle = variant.title?.toLowerCase() || '';
      const options = variant.selectedOptions || [];
      const supplierOption = options.find((opt: any) => 
        opt.name?.toLowerCase() === 'supplier' || 
        opt.name?.toLowerCase() === 'vendor'
      );
      
      const supplierValue = supplierOption?.value?.toLowerCase() || '';
      
      // Determine if this is a Coles or Woolworths variant
      const isColes = variantTitle.includes('coles') || supplierValue.includes('coles');
      const isWoolworths = variantTitle.includes('woolworths') || 
                          variantTitle.includes('woolies') || 
                          supplierValue.includes('woolworths') ||
                          supplierValue.includes('woolies');
      
      // console.log(`Variant ${variant.title}: isColes=${isColes}, isWoolworths=${isWoolworths}`);
      
      // Extract price from the variant
      let price = 0; // Default price of 0 will show as N/A in the UI
      try {
        if (variant.price) {
          // Try to extract price from the Shopify variant
          if (typeof variant.price === 'object') {
            // Try to access the amount property if it exists
            if (variant.price.amount !== undefined) {
              const amount = variant.price.amount;
              price = typeof amount === 'string' ? parseFloat(amount) : amount;
            }
            // Try to access through attrs if available
            else if (variant.price.attrs && variant.price.attrs.amount) {
              price = parseFloat(variant.price.attrs.amount);
            }
            // Try using toString() which often returns the price value
            else {
              const priceStr = String(variant.price);
              if (priceStr && !isNaN(parseFloat(priceStr))) {
                price = parseFloat(priceStr);
              }
            }
          } else {
            // Direct access if it's already a primitive
            price = parseFloat(String(variant.price));
          }
        }
        
        // If price is NaN, set it to 0 so UI will show N/A
        if (isNaN(price)) {
          price = 0;
        }
        
        // console.log(`Extracted price for ${variant.title}: ${price > 0 ? '$' + price.toFixed(2) : 'N/A'}`);
      } catch (error) {
        console.error('Error extracting price:', error);
        price = 0; // Default to 0 so UI will show N/A
      }
      
      if (isColes) {
        // This is a Coles variant
        hasColesVariant = true;
        itemPricing.push({
          vendor_id: 1, // 1 for Coles
          vendor_product_code: variant.sku || variant.id,
          price: price,
          available: variant.available !== false,
          volume: '',
          special: false,
          // Optional fields
          special_type: '',
          special_text: '',
          special_original_price: 0,
          multibuy_price: 0,
          multibuy_quantity: 0
        });
        // console.log('Added Coles pricing:', price);
      } else if (isWoolworths) {
        // This is a Woolworths variant
        hasWoolworthsVariant = true;
        itemPricing.push({
          vendor_id: 2, // 2 for Woolworths
          vendor_product_code: variant.sku || variant.id,
          price: price,
          available: variant.available !== false,
          volume: '',
          special: false,
          // Optional fields
          special_type: '',
          special_text: '',
          special_original_price: 0,
          multibuy_price: 0,
          multibuy_quantity: 0
        });
        // console.log('Added Woolworths pricing:', price);
      }
    }
    
    // If we couldn't identify specific vendors from variants, use the metafields
    if (shopifyProduct.metafields) {
      // console.log('Checking product metafields');
      for (const metafield of shopifyProduct.metafields) {
        // Add null check before accessing properties
        if (metafield && metafield.key) {
          // console.log('Metafield:', metafield.key, metafield.value);
          // Try to extract pricing data from metafields
        }
      }
    }
    
    // If no specific vendor variants were found, create default pricing for both vendors
    if (itemPricing.length === 0) {
      // console.log('No vendor-specific variants found, creating default pricing');
      
      // Get the price from the first variant or use default prices
      let colesPrice = 0;
      let woolworthsPrice = 0;
      
      if (variants.length > 0) {
        try {
          const variant = variants[0];
          if (variant.price) {
            // Try to extract price from the variant
            let extractedPrice = 0;
            
            // Try different approaches to extract the price
            if (typeof variant.price === 'object') {
              // Try to access the amount property if it exists
              if (variant.price.amount !== undefined) {
                const amount = variant.price.amount;
                extractedPrice = typeof amount === 'string' ? parseFloat(amount) : amount;
              }
              // Try to access through attrs if available
              else if (variant.price.attrs && variant.price.attrs.amount) {
                extractedPrice = parseFloat(variant.price.attrs.amount);
              }
              // Try using toString() which often returns the price value
              else {
                const priceStr = String(variant.price);
                if (priceStr && !isNaN(parseFloat(priceStr))) {
                  extractedPrice = parseFloat(priceStr);
                }
              }
            } else {
              // Direct access if it's already a primitive
              extractedPrice = parseFloat(String(variant.price));
            }
            
            // Use extracted price if valid
            if (!isNaN(extractedPrice) && extractedPrice > 0) {
              // Set slightly different prices for Coles and Woolworths
              colesPrice = extractedPrice;
              woolworthsPrice = extractedPrice;
            }
          }
          // console.log('Using extracted prices:', colesPrice, woolworthsPrice);
        } catch (e) {
          console.error('Error extracting price from variant:', e);
        }
      }
      
      // Add Coles pricing
      itemPricing.push({
        vendor_id: 1, // Coles
        vendor_product_code: variants.length > 0 ? variants[0].sku || variants[0].id : '',
        price: colesPrice,
        available: true,
        volume: '',
        special: false
      });
      
      // Add Woolworths pricing
      itemPricing.push({
        vendor_id: 2, // Woolworths
        vendor_product_code: variants.length > 0 ? variants[0].sku || variants[0].id : '',
        price: woolworthsPrice,
        available: true,
        volume: '',
        special: false
      });
      
      hasColesVariant = true;
      hasWoolworthsVariant = true;
      
      // console.log('Added default pricing for both vendors:', colesPrice, woolworthsPrice);
    }
    
    // Extract the first image URL or use empty string if not available
    const imageUrl = shopifyProduct.images && shopifyProduct.images.length > 0 
      ? shopifyProduct.images[0].src 
      : '';

    // Check metafields for special price and half price indicators
    let isSpecial = false;
    let isHalfThePrice = false;
    
    if (shopifyProduct.metafields && Array.isArray(shopifyProduct.metafields)) {
      // console.log(`Product ${shopifyProduct.title} has ${shopifyProduct.metafields.length} metafields:`, shopifyProduct.metafields);
      
      // Find the metafields for special price and half price
      const onSpecialMetafield = shopifyProduct.metafields.find(
        (metafield: any) => 
          metafield && metafield.key === 'on_special'
      );
      
      const halfPriceMetafield = shopifyProduct.metafields.find(
        (metafield: any) => 
          metafield && metafield.key === 'is_half_price_or_better'
      );
      
      // Set the flags based on metafield values
      if (onSpecialMetafield) {
        // Convert string 'true'/'false' to boolean if needed
        const metafieldValue = String(onSpecialMetafield.value).toLowerCase();
        isSpecial = metafieldValue === 'true' || metafieldValue === 'yes' || metafieldValue === '1';
        
        // console.log(`Product ${shopifyProduct.title} on_special metafield:`, onSpecialMetafield.value, '→', isSpecial);
      }
      
      if (halfPriceMetafield) {
        // Convert string 'true'/'false' to boolean if needed
        const metafieldValue = String(halfPriceMetafield.value).toLowerCase();
        isHalfThePrice = metafieldValue === 'true' || metafieldValue === 'yes' || metafieldValue === '1';
        
        // console.log(`Product ${shopifyProduct.title} half price metafield:`, halfPriceMetafield.value, '→', isHalfThePrice);
      }
      
      // Log all metafields to help debug
      // console.log(`All metafields for ${shopifyProduct.title}:`, shopifyProduct.metafields.map((m: any) => m ? `${m.namespace}:${m.key}` : 'null').join(', '));
      
      // Find the Data metafield that contains unit pricing information
      // Check for various possible keys and namespaces
      const dataMetafield = shopifyProduct.metafields.find(
        (metafield: any) => 
          metafield && 
          (metafield.key === 'Data' || 
           metafield.key === 'data' || 
           metafield.key === 'json' || 
           metafield.key === 'unit_pricing')
      );
      
      if (dataMetafield) {
        // console.log(`Found potential Data metafield for ${shopifyProduct.title}:`, {
        //   namespace: dataMetafield.namespace,
        //   key: dataMetafield.key,
        //   valuePreview: dataMetafield.value ? dataMetafield.value.substring(0, 50) + '...' : 'null'
        // });
      } else {
        // console.log(`No Data metafield found for ${shopifyProduct.title}`);
      }
      
      // If we have a Data metafield, try to extract unit pricing information
      if (dataMetafield && dataMetafield.value) {
        try {
          // Try to parse the JSON data
          let dataJson;
          try {
            dataJson = JSON.parse(dataMetafield.value);
          } catch (e) {
            // If direct parsing fails, try to clean the string first
            // Sometimes metafield values have escaped quotes or other characters
            const cleanedValue = dataMetafield.value
              .replace(/\\\\\\"|\\\"|\\"|\"/g, '"') // Handle various levels of escaped quotes
              .replace(/\\n/g, '') // Remove newlines
              .replace(/\\r/g, '') // Remove carriage returns
              .replace(/\\t/g, '') // Remove tabs
              .trim();
            dataJson = JSON.parse(cleanedValue);
          }
          
          // console.log(`Found Data metafield for ${shopifyProduct.title}:`, dataJson);
          
          // Extract unit pricing for Coles and Woolworths
          for (const pricing of itemPricing) {
            if (pricing.vendor_id === 1) { // Coles
              // Try different possible paths to the cup price data
              const colesData = dataJson.Coles || dataJson.coles;
              if (colesData) {
                // Always populate cup price even if just the volume exists without the price
                if (colesData.cup) {
                  if (colesData.cup.price) {
                    // Divide by 100 to fix decimal point issue
                    pricing.cup_price = parseFloat(colesData.cup.price) / 100;
                  }
                  
                  // Prioritize cupString, then try to combine cup.price and cup.volume for display
                  let cupVolumeText = '';
                  if (colesData.cupString) {
                    cupVolumeText = colesData.cupString;
                  } else if (colesData.cup.volume) {
                    cupVolumeText = colesData.cup.volume;
                  }
                  
                  // Format cup volume to lowercase or more user-friendly text
                  pricing.cup_volume = formatCupVolume(cupVolumeText);
                  
                  console.log(`Coles cup volume from metadata:`, { 
                    cupString: colesData.cupString, 
                    cupVolume: colesData.cup ? colesData.cup.volume : undefined, 
                    final: pricing.cup_volume 
                  });
                  console.log('Coles full metadata structure:', JSON.stringify(colesData, null, 2));
                }
                
                // Extract wasPrice if available (for sale indicators)
                if (colesData.wasPrice !== undefined && colesData.wasPrice !== null) {
                  // Convert wasPrice to number and divide by 100 if needed
                  const wasPrice = parseFloat(String(colesData.wasPrice)) / 100;
                  pricing.was_price = wasPrice > 0 ? wasPrice : undefined;
                  console.log(`Added Coles wasPrice: $${pricing.was_price}`);
                }
                
                // Extract multi-buy deal data if available
                if (colesData.hasMultiBuyDeal && colesData.hasMultiBuyDeal.deal) {
                  const deal = colesData.hasMultiBuyDeal.deal;
                  pricing.multibuy_quantity = deal.quantity;
                  // Handle both spellings (price and pice) - there's a typo in the Shopify data
                  pricing.multibuy_price = deal.price || deal.pice;
                  pricing.multibuy_description = deal.description;
                  console.log(`Added Coles multi-buy deal: ${deal.quantity} for $${pricing.multibuy_price}`);
                }
              }
            } else if (pricing.vendor_id === 2) { // Woolworths
              // Try different possible paths to the cup price data
              const woolworthsData = dataJson.Woolworths || dataJson.woolworths;
              if (woolworthsData) {
                // Always populate cup price even if just the volume exists without the price
                if (woolworthsData.cup) {
                  if (woolworthsData.cup.price) {
                    // Divide by 100 to fix decimal point issue
                    pricing.cup_price = parseFloat(woolworthsData.cup.price) / 100;
                  }
                  
                  // Prioritize cupString, then try to combine cup.price and cup.volume for display
                  let cupVolumeText = '';
                  if (woolworthsData.cupString) {
                    cupVolumeText = woolworthsData.cupString;
                  } else if (woolworthsData.cup.volume) {
                    cupVolumeText = woolworthsData.cup.volume;
                  }
                  
                  // Format cup volume to lowercase or more user-friendly text
                  pricing.cup_volume = formatCupVolume(cupVolumeText);
                  
                  console.log(`Woolworths cup volume from metadata:`, { 
                    cupString: woolworthsData.cupString, 
                    cupVolume: woolworthsData.cup ? woolworthsData.cup.volume : undefined, 
                    final: pricing.cup_volume 
                  });
                  console.log('Woolworths full metadata structure:', JSON.stringify(woolworthsData, null, 2));
                }
                
                // Extract wasPrice if available (for sale indicators)
                if (woolworthsData.wasPrice !== undefined && woolworthsData.wasPrice !== null) {
                  // Convert wasPrice to number and divide by 100 if needed
                  const wasPrice = parseFloat(String(woolworthsData.wasPrice)) / 100;
                  pricing.was_price = wasPrice > 0 ? wasPrice : undefined;
                  console.log(`Added Woolworths wasPrice: $${pricing.was_price}`);
                }
                
                // Extract multi-buy deal data if available
                if (woolworthsData.hasMultiBuyDeal && woolworthsData.hasMultiBuyDeal.deal) {
                  const deal = woolworthsData.hasMultiBuyDeal.deal;
                  pricing.multibuy_quantity = deal.quantity;
                  pricing.multibuy_price = deal.price;
                  pricing.multibuy_description = deal.description;
                  console.log(`Added Woolworths multi-buy deal: ${deal.quantity} for $${deal.price}`);
                }
              }
            }
          }
          
          // Also check for multi-buy deals at the root level of the data
          if (dataJson.hasMultiBuyDeal) {
            const vendorName = dataJson.hasMultiBuyDeal.vendor?.toLowerCase();
            if (vendorName) {
              const deal = dataJson.hasMultiBuyDeal.deal;
              if (deal) {
                // Find the corresponding vendor pricing
                const vendorId = vendorName.includes('coles') ? 1 : vendorName.includes('woolworths') ? 2 : 0;
                if (vendorId > 0) {
                  const vendorPricing = itemPricing.find(p => p.vendor_id === vendorId);
                  if (vendorPricing) {
                    vendorPricing.multibuy_quantity = deal.quantity;
                    vendorPricing.multibuy_price = deal.price;
                    vendorPricing.multibuy_description = deal.description;
                    console.log(`Added ${vendorName} multi-buy deal from root level: ${deal.quantity} for $${deal.price}`);
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error('Error parsing Data metafield JSON:', error);
        }
      }
    } else {
      console.log(`Product ${shopifyProduct.title} has no metafields`);
    }

    // Transform to the expected ItemData format
    const itemData: ItemData = {
      id: numericId,
      crawler_id: numericId,
      name: shopifyProduct.title || 'Unknown Product',
      image_url: imageUrl,
      item_pricing: itemPricing,
      selected_vendor_id: itemPricing[0]?.vendor_id || 1, // Select first vendor as default
      quantity: 1,
      close_match_id: null,
      close_match_a: null,
      isSpecial: Boolean(isSpecial), // Ensure boolean type
      isHalfThePrice: Boolean(isHalfThePrice), // Ensure boolean type
      matchingItemsOnly: false,
      exclusiveColes: hasColesVariant && !hasWoolworthsVariant,
      exclusiveWools: !hasColesVariant && hasWoolworthsVariant,
      availableAtBoth: hasColesVariant && hasWoolworthsVariant,
      // Add these methods to satisfy the interface
      split(arg0: string) { return []; },
      includes(arg0: string) { return false; }
    };

    // console.log(`Transformed ${shopifyProduct.title} with ${itemPricing.length} pricing options:`, 
    //   itemPricing.map(p => `${p.vendor_id === 1 ? 'Coles' : 'Woolworths'}: ${p.price > 0 ? '$' + p.price.toFixed(2) : 'N/A'}`).join(', '));
    
    // Debug the final ItemData object's special indicators
    // console.log(`FINAL ItemData for ${shopifyProduct.title}: isSpecial=${itemData.isSpecial}, isHalfThePrice=${itemData.isHalfThePrice}`);
    
    // console.log('TRANSFORMER: Successfully transformed product:', itemData.id, itemData.name);
    // console.log('TRANSFORMER: Item pricing count:', itemData.item_pricing.length);
    // console.log('TRANSFORMER: Special indicators:', { isSpecial: itemData.isSpecial, isHalfThePrice: itemData.isHalfThePrice });
    return itemData;
  } catch (error) {
    console.error('Error transforming Shopify product:', error, shopifyProduct);
    // Return a minimal valid ItemData object to prevent app crashes
    return {
      id: Math.floor(Math.random() * 1000000),
      crawler_id: 0,
      name: 'Error loading product',
      image_url: '',
      item_pricing: [{
        vendor_id: 1,
        price: 0, // Default price instead of 4.50
        volume: '',
        special: false
      }, {
        vendor_id: 2,
        price: 0, // Default price instead of 4.50
        volume: '',
        special: false
      }],
      selected_vendor_id: 1,
      quantity: 1,
      close_match_id: null,
      close_match_a: null,
      split(arg0: string) { return []; },
      includes(arg0: string) { return false; }
    };
  }
};

/**
 * Transforms an array of Shopify products into an array of ItemData objects
 */
export const transformShopifyProductsToItemDataArray = (shopifyProducts: any[]): ItemData[] => {
  // console.log(`TRANSFORMER ARRAY: Transforming ${shopifyProducts.length} Shopify products to ItemData array`);
  
  if (!shopifyProducts || shopifyProducts.length === 0) {
    console.log('TRANSFORMER ARRAY: WARNING - No products to transform');
    return [];
  }
  
  if (!Array.isArray(shopifyProducts)) {
    console.error('shopifyProducts is not an array:', shopifyProducts);
    return [];
  }
  
  const transformedProducts = shopifyProducts.map(product => {
    try {
      return transformShopifyProductToItemData(product);
    } catch (error) {
      console.error('Error transforming product:', error);
      return null;
    }
  }).filter(product => product !== null) as ItemData[];
  
  console.log(`TRANSFORMER ARRAY: Successfully transformed ${transformedProducts.length} products`);
  
  if (transformedProducts.length === 0) {
    console.log('TRANSFORMER ARRAY: WARNING - No products were successfully transformed');
  } else {
    console.log('TRANSFORMER ARRAY: First transformed product:', transformedProducts[0]);
  }
  return transformedProducts;
};
